import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { Category } from '../../types';
import { categoriesApi } from '../../firebase/services';
import TranslationTabs, { TranslationValues } from './TranslationTabs';

interface CategoryManagementProps {
  categories: Category[];
  onRefresh: () => void;
}

const CategoryManagement: React.FC<CategoryManagementProps> = ({ categories, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Category | null>(null);
  const [name, setName] = useState('');
  const [translations, setTranslations] = useState<TranslationValues>({ fr: {}, es: {} });

  const handleOpen = (category?: Category) => {
    if (category) {
      setSelected(category);
      setName(category.name);
      setTranslations({
        fr: { name: category.translations?.fr?.name || '' },
        es: { name: category.translations?.es?.name || '' },
      });
    } else {
      setSelected(null);
      setName('');
      setTranslations({ fr: {}, es: {} });
    }
    setError(null);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Le nom de la catégorie est requis');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data: Omit<Category, 'id'> = { name: name.trim(), translations };
      if (selected) {
        await categoriesApi.update(selected.id, data);
      } else {
        await categoriesApi.create(data);
      }
      onRefresh();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDelete = (category: Category) => {
    setSelected(category);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selected) return;
    setIsLoading(true);
    try {
      await categoriesApi.delete(selected.id);
      onRefresh();
      setDeleteDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ color: '#495057', fontWeight: 600 }}>
          <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Catégories de services
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Ajouter une catégorie
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Nom (EN)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Français</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Español</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#6c757d' }}>
                  Aucune catégorie. Ajoutez votre première catégorie !
                </TableCell>
              </TableRow>
            ) : (
              categories.map((cat) => (
                <TableRow key={cat.id} hover>
                  <TableCell>
                    <Typography fontWeight={500}>{cat.name}</Typography>
                  </TableCell>
                  <TableCell>{cat.translations?.fr?.name || <Typography color="text.disabled" variant="body2">—</Typography>}</TableCell>
                  <TableCell>{cat.translations?.es?.name || <Typography color="text.disabled" variant="body2">—</Typography>}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpen(cat)} sx={{ color: '#1976d2' }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDelete(cat)} sx={{ color: '#d32f2f' }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selected ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            label="Nom de la catégorie (EN) *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="dense"
            autoFocus
          />
          <TranslationTabs
            fields={[{ key: 'name', label: 'Nom de la catégorie' }]}
            values={translations}
            onChange={setTranslations}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>Annuler</Button>
          <Box sx={{ position: 'relative' }}>
            <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
              {selected ? 'Mettre à jour' : 'Ajouter'}
            </Button>
            {isLoading && (
              <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }} />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer la catégorie</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer la catégorie <strong>"{selected?.name}"</strong> ?
            Les services existants dans cette catégorie ne seront pas supprimés.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isLoading}>Annuler</Button>
          <Box sx={{ position: 'relative' }}>
            <Button onClick={handleDelete} variant="contained" color="error" disabled={isLoading}>
              Supprimer
            </Button>
            {isLoading && (
              <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }} />
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement;
